import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from 'src/auth/context/authContext';
import { logoutAllStores } from 'src/redux/logoutStore';
import { useAxiosAuthInterceptor } from 'src/hooks/useAxiosAuthInterceptor';

import { axiosInstanceNew } from 'src/utils/axios';
import { initCsrf, resetCsrfGate } from 'src/utils/csrf';
// axiosInstanceNew is passed explicitly to initCsrf and resetCsrfGate
// to avoid a circular dependency between csrf.js and axios.js.
import { handleAdminLogoutSocket, handleClientLogoutSocket } from 'src/utils/socket';

// NOTE ON CHANGES FOR LP SUPPORT:
// - Added `lp` / `isLpAuthenticated` as a third identity, parallel to
//   user/admin throughout: clearAuthState, applySessionState, syncSession,
//   and contextValue all now handle it.
// - applySessionState's precedence is user > admin > lp > none when multiple
//   are somehow present at once (shouldn't normally happen, but keeps the
//   function deterministic).
// - Relies on /auth/me returning { lp: {...} } when an LP session is active —
//   confirmed already done in the auth-me-route.js update.
// - Did NOT add a handleLpLogoutSocket() call to disconnectAllSockets, since
//   LP doesn't have a socket-based real-time flow yet.
//
// GLITCH FIXES:
// - logout now clears BOTH redux stores (admin data used to survive an admin
//   logout and flash on the next login).
// - logout is re-entrancy safe (double taps / 401 storms run it once).
// - the session-expiry 401 interceptor is mounted here, once, for the whole
//   app. It only acts while someone is signed in.

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [lp, setLp] = useState(null);

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isLpAuthenticated, setIsLpAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Stable callbacks ──────────────────────────────────────────────────
  // All callbacks below close only over React state setters and imported
  // functions — both of which are guaranteed stable references. Empty deps
  // makes that contract explicit and prevents reference churn that was
  // causing the bootstrap effect to re-run and double-fetch the CSRF token.

  const clearAuthState = useCallback(() => {
    setUser(null);
    setAdmin(null);
    setLp(null);
    setIsUserAuthenticated(false);
    setIsAdminAuthenticated(false);
    setIsLpAuthenticated(false);
  }, []); // stable — only calls state setters

  const disconnectAllSockets = useCallback(async () => {
    await Promise.allSettled([handleClientLogoutSocket(), handleAdminLogoutSocket()]);
  }, []); // stable — only calls imported functions

  const applySessionState = useCallback(
    ({ user: userObj = null, admin: adminObj = null, lp: lpObj = null } = {}) => {
      if (userObj) {
        setUser(userObj);
        setAdmin(null);
        setLp(null);
        setIsUserAuthenticated(true);
        setIsAdminAuthenticated(false);
        setIsLpAuthenticated(false);
        return userObj;
      }

      if (adminObj) {
        setAdmin(adminObj);
        setUser(null);
        setLp(null);
        setIsAdminAuthenticated(true);
        setIsUserAuthenticated(false);
        setIsLpAuthenticated(false);
        return adminObj;
      }

      if (lpObj) {
        setLp(lpObj);
        setUser(null);
        setAdmin(null);
        setIsLpAuthenticated(true);
        setIsUserAuthenticated(false);
        setIsAdminAuthenticated(false);
        return lpObj;
      }

      clearAuthState();
      return null;
    },
    [clearAuthState] // clearAuthState is now stable so this is also stable
  );

  const syncSession = useCallback(
    async ({ disconnectSocketsOnFail = true } = {}) => {
      try {
        const { data } = await axiosInstanceNew.get('/auth/me');

        const userObj = data?.user ?? null;
        const adminObj = data?.admin ?? null;
        const lpObj = data?.lp ?? null;

        if (!userObj && !adminObj && !lpObj) {
          clearAuthState();
          if (disconnectSocketsOnFail) disconnectAllSockets();
          return null;
        }

        return applySessionState({ user: userObj, admin: adminObj, lp: lpObj });
      } catch (error) {
        clearAuthState();
        if (disconnectSocketsOnFail) disconnectAllSockets();
        return null;
      }
    },
    [applySessionState, clearAuthState, disconnectAllSockets]
    // All deps are stable so syncSession is also stable.
  );

  const verifyUserSession = useCallback(
    async (options = {}) => {
      return syncSession({
        disconnectSocketsOnFail: options.disconnectSocketsOnFail ?? true,
      });
    },
    [syncSession]
  );

  // ── syncSession ref ───────────────────────────────────────────────────
  // Holds the latest syncSession so the bootstrap effect can call it
  // without listing it as a dependency. This breaks the cycle where a new
  // syncSession reference (from a re-render triggered by setLoading(true))
  // caused the bootstrap effect to re-run and fire initCsrf() a second time,
  // producing two CSRF requests and invalidating the downstream calls.
  const syncSessionRef = useRef(syncSession);
  useEffect(() => {
    syncSessionRef.current = syncSession;
  });

  // ── Initial session bootstrap ─────────────────────────────────────────
  // Empty deps — runs exactly once on mount.
  //
  // WHY EMPTY DEPS (previously [syncSession]):
  // The effect had syncSession in its deps. setLoading(true) inside
  // fetchSession triggered a re-render which produced a new syncSession
  // reference, which re-ran the effect, which called initCsrf() again.
  // This caused two CSRF fetches in quick succession — the first token was
  // consumed before the second request could use it, making contract-balance
  // and profit-percent calls return 403s.
  //
  // syncSessionRef.current is used instead so the effect always calls the
  // latest syncSession without re-running when its reference changes.
  //
  // Order matters:
  //   1. initCsrf()    — fetches token, attaches header, resolves the gate
  //   2. syncSession() — /auth/me now has the CSRF header attached
  //   3. setLoading(false) — unblocks the UI
  useEffect(() => {
    let cancelled = false;

    const fetchSession = async () => {
      setLoading(true);

      try {
        // Pass the axios instance so csrf.js doesn't need to import it
        // directly — breaking the circular dependency that caused
        // axiosInstanceNew to be undefined inside csrf.js.
        await initCsrf(axiosInstanceNew);
        if (cancelled) return;

        await syncSessionRef.current({ disconnectSocketsOnFail: true });
      } catch (error) {
        console.error('Session bootstrap failed', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSession();

    return () => {
      cancelled = true;
    };
  }, []); // ← empty deps: bootstrap runs exactly once on mount

  // ── Logout ────────────────────────────────────────────────────────────
  // ORDER MATTERS — resetCsrfGate must be called AFTER the logout POST.
  //
  // WHY AFTER (previously was before):
  // resetCsrfGate creates a new unresolved csrfReady promise. attachCsrfGate
  // makes every non-CSRF, non-bypass request await that promise. Calling
  // resetCsrfGate first caused the logout POST to hit the gate, find a fresh
  // unresolved promise, and hang forever — the server session was never
  // invalidated. On refresh the cookie was still valid and the user appeared
  // still logged in.
  //
  // The logout POST uses _bypassCsrfGate: true so it skips the gate entirely
  // and fires immediately with the current valid token still in memory.
  // Only after the POST completes (session destroyed server-side) do we reset
  // the gate and clear the token for the next login cycle.
  const logoutInFlightRef = useRef(null);

  const performLogout = useCallback(async () => {
    // 1. Clear frontend state immediately for snappier UX.
    clearAuthState();

    try {
      await logoutAllStores();
    } catch (error) {
      console.error('Store reset on logout failed', error);
    }

    // 2. POST /auth/logout — must fire BEFORE resetCsrfGate.
    //    _bypassCsrfGate skips the attachCsrfGate interceptor so this
    //    request is not blocked by csrfReady. The current CSRF token is
    //    still in memory and attached to axios defaults so the server
    //    accepts the request normally.
    try {
      await axiosInstanceNew.post(
        '/auth/logout',
        {},
        {
          withCredentials: true,
          _bypassCsrfGate: true,
        }
      );
    } catch (error) {
      console.error('Logout failed', error);
    } finally {
      await disconnectAllSockets();
    }

    // 3. Reset the CSRF gate AFTER the logout POST has completed.
    //    The server session is now destroyed. Resetting here creates the
    //    fresh unresolved gate that will block requests until the next
    //    initCsrf() call on the next login cycle.
    resetCsrfGate(axiosInstanceNew);

    try {
      await initCsrf(axiosInstanceNew);
    } catch (error) {
      // Non-fatal: the request gate fetches a token on the next request.
      console.error('CSRF re-init after logout failed', error);
    }
  }, [clearAuthState, disconnectAllSockets]);

  const logout = useCallback(async () => {
    if (logoutInFlightRef.current) return logoutInFlightRef.current;

    const run = (async () => {
      try {
        await performLogout();
      } finally {
        logoutInFlightRef.current = null;
      }
    })();

    logoutInFlightRef.current = run;
    return run;
  }, [performLogout]);

  useAxiosAuthInterceptor(
    logout,
    loading,
    isUserAuthenticated || isAdminAuthenticated || isLpAuthenticated
  );

  const prepareAuthRequests = useCallback(async () => {
    try {
      await initCsrf(axiosInstanceNew);
      return true;
    } catch (error) {
      console.error('Failed to prepare auth requests', error);
      return false;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user,
      admin,
      lp,
      verifyUserSession,
      prepareAuthRequests,
      loading,
      setLoading,
      setUser,
      setAdmin,
      setLp,
      isUserAuthenticated,
      isAdminAuthenticated,
      isLpAuthenticated,
      setIsUserAuthenticated,
      setIsAdminAuthenticated,
      setIsLpAuthenticated,
      logout,
    }),
    [
      user,
      admin,
      lp,
      verifyUserSession,
      prepareAuthRequests,
      loading,
      isUserAuthenticated,
      isAdminAuthenticated,
      isLpAuthenticated,
      logout,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};
